import { useState } from 'react';
import { Alert, Button, Collapse, Modal, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { apiErrorMessage } from '@/api/client';
import { getInputRecords, type InputRecord } from '@/api/inputRecords';
import { useSessionStore } from '@/store/session';

const STATUS_LABELS: Record<string, string> = { RECEIVED: '处理中', COMPLETED: '已保存', FAILED: '解析或提交失败' };

const LABELS: Record<string, string> = {
  'input-records/paste': '原始粘贴内容',
  'payees/ai-extract': 'AI 收款人解析',
  'payees/create': '确认创建收款人',
  'payout-applications/ai-extract': 'AI 申请解析',
  'payout-applications/ai-create': '确认创建申请',
  'payout-applications/create': '手动创建申请',
  'payout-applications/ai-batch/detect-header': '表头识别',
  'payout-applications/ai-batch/parse-workbook': '原始 Excel',
  'payout-applications/ai-batch/create': '确认批量创建',
  'payout-imports/parse': '原始 Excel 与预览',
  'payout-imports/commit': '确认模板导入',
  'batch-row': '本条确认值',
  'collection/submit': '外部表单原始提交',
};

function JsonView({ value }: { value: unknown }) {
  return <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 380, overflow: 'auto', fontSize: 12 }}>{JSON.stringify(value, null, 2)}</pre>;
}

function OriginalInput({ value }: { value: unknown }) {
  const input = value as { encoding?: string; content?: string; filename?: string; text?: string } | null;
  if (input?.encoding === 'base64' && input.content) {
    return <Button onClick={() => {
      const bytes = Uint8Array.from(atob(input.content!), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }));
      const link = document.createElement('a'); link.href = url; link.download = input.filename ?? 'original.xlsx'; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }}>下载原始文件：{input.filename ?? 'original.xlsx'}</Button>;
  }
  if (typeof input?.text === 'string') return <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }} copyable>{input.text}</Typography.Paragraph>;
  return <JsonView value={value} />;
}

export default function InputRecords({ payeeId, applicationId }: { payeeId?: string; applicationId?: string }) {
  const allowed = useSessionStore((s) => s.me?.roles.some((r) => r === 'admin' || r === 'finance'));
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<InputRecord[]>([]);
  const [error, setError] = useState('');
  if (!allowed) return null;
  async function show() {
    setOpen(true); setLoading(true); setRecords([]); setError('');
    try { setRecords(await getInputRecords(applicationId ? { applicationId } : { payeeId: payeeId! })); }
    catch (err) { setError(apiErrorMessage(err, '原始记录读取失败')); }
    finally { setLoading(false); }
  }
  return <>
    <Button size="small" onClick={() => void show()}>查看原始记录</Button>
    <Modal open={open} title="原始录入记录" width={900} onCancel={() => setOpen(false)} footer={null} destroyOnHidden>
      {loading ? <Spin /> : error ? <Alert type="error" title={error} /> : records.length === 0 ?
        <Alert type="info" title="这条历史数据未留存原始记录" description="原始留存功能上线前的导入原文无法补回。" /> :
        <Collapse items={records.map((record) => ({ key: record.id,
          label: `${LABELS[record.kind] ?? record.kind} · ${dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss')}`,
          children: <Space orientation="vertical" style={{ width: '100%' }}>
            <Typography.Text type="secondary">{STATUS_LABELS[record.status] ?? record.status}{record.errorCode ? ` · ${record.errorCode}` : ''}</Typography.Text>
            <Typography.Text strong>原始输入</Typography.Text><OriginalInput value={record.input} />
            {record.output != null && <Collapse style={{ width: '100%' }} items={[{ key: 'output', label: '当时保存的结果', children: <JsonView value={record.output} /> }]} />}
            {record.aiCalls.length > 0 && <Collapse items={record.aiCalls.map((call, i) => ({ key: i, label: `AI 第 ${i + 1} 次返回 · ${call.model}`, children: <JsonView value={call} /> }))} />}
          </Space>,
        }))} />}
    </Modal>
  </>;
}
